import type { SiteHost } from "./host.server";

type Mode = SiteHost["mode"] | undefined;

export const cookiesStayOnHost = (mode: Mode) => mode !== "path";

/*
 * Safari, among others, doesn't treat http://*.localhost as secure, so it
 * drops Secure cookies there, __Host- ones included. Only on a development
 * host over plain http do the site's cookies go without both.
 */
export function isPlainHttpDevHost(request: Request): boolean {
  const url = new URL(request.url);
  if (url.protocol !== "http:") return false;
  const host = (request.headers.get("host") || url.host)
    .toLowerCase()
    .replace(/:\d+$/, "");
  return host.endsWith(".localhost");
}

/** The site's own cookie names for this request, the one to write first. */
export const siteCookieNames = (request: Request, name: string) =>
  isPlainHttpDevHost(request)
    ? [`site-${name}`, `__Host-${name}`]
    : [`__Host-${name}`];

export const segmentCookie = (hostOnly: boolean) =>
  hostOnly ? { domain: undefined } : undefined;

export const amplitudeCookieOptions = (hostOnly: boolean) =>
  hostOnly ? { domain: "" } : undefined;

export function withoutDomain(cookie: string): string {
  const [pair, ...attributes] = cookie.split(";");
  return [pair, ...attributes.filter((a) => !/^\s*domain\s*=/i.test(a))].join(
    ";"
  );
}

// Segment and Stripe scripts pick the widest cookie domain and offer no option to stop them.
export function keepCookiesOnHost(doc: Document = document): void {
  if (Object.getOwnPropertyDescriptor(doc, "cookie")) return;
  const native = Object.getOwnPropertyDescriptor(Document.prototype, "cookie");
  const { get, set } = native ?? {};
  if (!get || !set) return;
  Object.defineProperty(doc, "cookie", {
    configurable: true,
    get: () => get.call(doc),
    set: (value: string) => set.call(doc, withoutDomain(String(value))),
  });
}

export function pageCookiesStayOnHost(): boolean {
  if (typeof window === "undefined") return false;
  const hostOnly = cookiesStayOnHost(window.ENV?.SITE_MODE);
  if (hostOnly) keepCookiesOnHost();
  return hostOnly;
}
